import axiosInstance from '@/axios/axios';
import { useQuery } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { getClassesArray } from '@/lib/class-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AlertCircle, Users, Printer, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface User {
  first_name: string;
  last_name: string;
  standard?: string;
  division?: string;
  username: string;
  tempCode: string | null;
}

interface ApiResponse {
  status: number;
  message: string;
  users?: User[];
}

const AdminTempCodes = () => {
  const [standard, setStandard] = useState('Play Group');
  const [division, setDivision] = useState('A');
  const [userType, setUserType] = useState('student');
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const printRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error } = useQuery<ApiResponse>({
    queryKey: ['admin-temp-codes', userType, standard, division, fetchTrigger],
    queryFn: async () => {
      const payload = {
        user_type: userType,
        ...(userType === 'student' && { std: standard, div: division }),
      };

      const res = await axiosInstance.post('/get-temp-codes/', payload);
      return res.data;
    },
    enabled: fetchTrigger > 0,
  });

  const handleFetch = () => {
    setFetchTrigger((prev) => prev + 1);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${userType.charAt(0).toUpperCase() + userType.slice(1)}s${
      userType === 'student' ? ` - Standard ${standard} Division ${division}` : ''
    }`,
  });

  // Generate standards from Play Group to 10 using class-utils
  const standardOptions = getClassesArray('Play Group', '10');
  const divisionOptions = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
  const userTypeOptions = [
    { value: 'student', label: 'Students' },
    { value: 'teacher', label: 'Teachers' },
    { value: 'school_admin', label: 'Admins' },
  ];

  const users = data?.users || [];

  return (
    <div className="route-container space-y-6">
      <div className="flex items-center gap-2">
        <Users className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Temp Codes Management</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Filter Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="userType">User Type</Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  {userTypeOptions.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {userType === 'student' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="standard">Standard</Label>
                  <Select value={standard} onValueChange={setStandard}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select standard" />
                    </SelectTrigger>
                    <SelectContent>
                      {standardOptions.map((std) => (
                        <SelectItem key={std} value={std}>
                          {std}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="division">Division</Label>
                  <Select value={division} onValueChange={setDivision}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {divisionOptions.map((div) => (
                        <SelectItem key={div} value={div}>
                          {div}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="flex items-end">
              <Button onClick={handleFetch} disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Fetch {userTypeOptions.find((type) => type.value === userType)?.label}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            An error occurred while fetching{' '}
            {userTypeOptions.find((type) => type.value === userType)?.label.toLowerCase()}.
          </AlertDescription>
        </Alert>
      )}

      {data && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>{data.message}</CardTitle>
                {userType === 'student' && (
                  <p className="text-sm text-gray-600 mt-1">
                    Standard: {standard} | Division: {division}
                  </p>
                )}
              </div>
              {users.length > 0 && (
                <Button onClick={handlePrint} variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Table
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No {userTypeOptions.find((type) => type.value === userType)?.label} Found
                </h3>
                <p className="text-gray-600 max-w-sm">
                  {userType === 'student'
                    ? `No students were found for Standard ${standard} Division ${division}. Try selecting a different standard or division.`
                    : `No ${userTypeOptions
                        .find((type) => type.value === userType)
                        ?.label.toLowerCase()} were found.`}
                </p>
              </div>
            ) : (
              <div ref={printRef}>
                <style>
                  {`
                  @media print {
                    html, body {
                      height: initial !important;
                      overflow: initial !important;
                      -webkit-print-color-adjust: exact;
                    }

                    @page {
                      size: auto;
                      margin: 0.5in;
                    }

                    .print-table {
                      width: 100% !important;
                      border-collapse: collapse !important;
                      break-inside: auto !important;
                    }

                    .print-table thead {
                      display: table-header-group !important;
                    }

                    .print-table tbody {
                      display: table-row-group !important;
                    }

                    .print-table tr {
                      break-inside: avoid !important;
                      page-break-inside: avoid !important;
                    }

                    .print-table th,
                    .print-table td {
                      border: 1px solid #000 !important;
                      padding: 8px !important;
                      font-size: 11px !important;
                    }

                    .print-table th {
                      background-color: #f5f5f5 !important;
                      font-weight: bold !important;
                    }

                    .print-header {
                      margin-bottom: 20px !important;
                    }

                    .print-header h2 {
                      font-size: 18px !important;
                      margin-bottom: 10px !important;
                      text-align: center !important;
                    }

                    .print-header p {
                      font-size: 14px !important;
                      margin: 5px 0 !important;
                      text-align: center !important;
                    }
                  }
                  `}
                </style>
                <div className="print-header mb-6">
                  <h2 className="text-xl font-bold text-center">
                    {userTypeOptions.find((type) => type.value === userType)?.label} List
                  </h2>
                  {userType === 'student' && (
                    <p className="text-center">
                      Standard: {standard} | Division: {division}
                    </p>
                  )}
                  <p className="text-center text-sm text-gray-600">Total: {users.length}</p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300 print-table">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left">Sr. No.</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">First Name</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Last Name</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Username</th>
                        {userType === 'student' && (
                          <>
                            <th className="border border-gray-300 px-4 py-2 text-left">Standard</th>
                            <th className="border border-gray-300 px-4 py-2 text-left">Division</th>
                          </>
                        )}
                        <th className="border border-gray-300 px-4 py-2 text-left">Temp Code</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, index) => (
                        <tr
                          key={user.username}
                          className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="border border-gray-300 px-4 py-2">{index + 1}</td>
                          <td className="border border-gray-300 px-4 py-2 capitalize">
                            {user.first_name}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 capitalize">
                            {user.last_name}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 font-mono">
                            {user.username}
                          </td>
                          {userType === 'student' && (
                            <>
                              <td className="border border-gray-300 px-4 py-2">{user.standard}</td>
                              <td className="border border-gray-300 px-4 py-2">{user.division}</td>
                            </>
                          )}
                          <td className="border border-gray-300 px-4 py-2 font-mono font-semibold">
                            {user.tempCode || <span className="text-gray-400 italic">N/A</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminTempCodes;
